import * as React from 'react';

import {useSelector} from '@xstate/react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {Card} from '@spaced-out/ui-design-system/lib/components/Card';

import {RootMachineContext} from '../../machines/rootMachine';
import {useBookingMachine} from '../../hooks/useBookingMachine';
import {selectBookingInfo} from '../../selectors/booking-selector';
import {selectBrandSettings} from '../../selectors/brandSelectors';

import {Loader} from '../../lib/loader/loader';
import {BookingForm} from '../booking-form/booking-form';
import {BookingInfo} from '../../lib/booking-info/booking-info';
import {BookingFormBackButton} from '../booking-form/booking-form-back-button';
import {UnavailableTimeSlot} from '../unavailable-timeslot/unavailable-timeslot';
import {BookingDateTimePicker} from '../booking-date-time-picker/booking-date-time-picker';
import {BookingNotifyHostForm} from '../booking-notify-host-form/booking-notify-host-form';
import {BookingRescheduleFormBackButton} from '../booking-reschedule/booking-reschedule-form-back';
import {BookingRescheduleForm} from '../booking-reschedule/booking-reschedule-form';
import {BookingRescheduleNextButton} from '../booking-reschedule/booking-reschedule-next';
import {BookingAvailabilityRangeError} from '../booking-errors/Booking-availability-range-error';

import css from './booking-layout.css';


export const BookingLayout = () => {
  const {t, makeKey, language} = useLabelTranslation();

  const {bookingActor, bookingState} = useBookingMachine();
  const rootState = RootMachineContext.useSelector((state) => state);
  const brandSettings = RootMachineContext.useSelector(selectBrandSettings);
  const bookingInfo = useSelector(bookingActor, (snapshot) =>
    selectBookingInfo(snapshot, t, language, makeKey),
  );

  const brandLogo = brandSettings?.logo || '';
  const bookingInfoItems = bookingInfo.bookingInfoItems;

  const isLoadingAvailabilityRange = bookingState.matches(
    'availabilityRangeLoading',
  );
  const hasUnavailableTimeSlot = bookingState.matches({
    bookingForm: 'unavailableTimeSlot',
  });
  const isBookingFormState = bookingState.matches('bookingForm');
  const isDateTimePickerState = bookingState.matches('dateTimePicker');
  const isNotifyHostState = bookingState.matches('notify');
  const isRescheduleReasonFormState = bookingState.matches('rescheduleForm');
  const hasUnavailableTimeSlotInRescheduleFlow = bookingState.matches({
    rescheduleForm: 'unavailableTimeSlotForReschedule',
  });
  const isTimeslotConfirmationState = bookingState.matches({
    dateTimePicker: 'timeslotConfirmation',
  });

  const isRescheduleFlow = rootState.matches('rescheduleFlow');

  const hasUnavailableTimeSlotForReschedule = bookingState.matches({
    rescheduleForm: 'unavailableTimeSlotForReschedule',
  });
  const hasAvailabilityRangeError = bookingState.matches(
    'availabilityRangeError',
  );

  return (
    <Card classNames={{wrapper: css.bookingLayoutContainer}}>
      <div className={css.bookingInfoLeftContainer}>
        {isBookingFormState && <BookingFormBackButton />}
        {isRescheduleReasonFormState && <BookingRescheduleFormBackButton />}
        <BookingInfo
          title={bookingInfo.title}
          brandLogo={brandLogo}
          bookingInfoItems={bookingInfoItems}
        />
      </div>
      <div className={css.bookingLayoutDivider} />
      <div className={css.bookingInfoRightContainer}>
        {isLoadingAvailabilityRange && <Loader />}
        {hasUnavailableTimeSlot && (
          <UnavailableTimeSlot isRescheduleFlow={false} />
        )}
        {hasUnavailableTimeSlotForReschedule && (
          <UnavailableTimeSlot isRescheduleFlow={isRescheduleFlow} />
        )}
        {hasAvailabilityRangeError && <BookingAvailabilityRangeError />}
        {isBookingFormState && !hasUnavailableTimeSlot && <BookingForm />}
        {isRescheduleReasonFormState &&
          !hasUnavailableTimeSlotInRescheduleFlow && <BookingRescheduleForm />}
        {isDateTimePickerState && <BookingDateTimePicker />}
        {isTimeslotConfirmationState && <BookingRescheduleNextButton />}
        {isNotifyHostState && <BookingNotifyHostForm />}
      </div>
    </Card>
  );
};
